import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

type Deal = {
	id: string;
	negotiationId: string;
	offerId: string;
	brandOrgId: string;
	affiliateOrgId: string;
	payoutType: "cpa" | "cpl" | "revshare" | "hybrid";
	payoutAmount: number | null;
	payoutPercent: number | null;
	payoutCurrency: string;
	contractWormUri: string;
	contractHash: string;
	state: "active" | "paused" | "completed" | "disputed" | "cancelled";
	createdAt: number;
	updatedAt: number;
};

type ListResp = { items: Deal[]; total: number };

export function Deals() {
	const { data, isLoading } = useQuery<ListResp>({
		queryKey: ["deals"],
		queryFn: () => api.get("/deals"),
	});

	if (isLoading) return <div className="p-6">Loading…</div>;
	const items = data?.items ?? [];

	return (
		<div className="space-y-3 p-6">
			<h1 className="text-xl font-semibold">Deals</h1>
			{items.length === 0 && (
				<div className="text-sm text-neutral-500">
					No signed deals yet. Once both sides sign a negotiation, the deal lands here.
				</div>
			)}
			{items.map((d) => (
				<Link
					key={d.id}
					to={`/deals/${d.id}`}
					className="block rounded border border-neutral-200 bg-white p-4 shadow-sm hover:bg-neutral-50"
				>
					<div className="flex items-center justify-between">
						<div>
							<div className="font-medium">
								{d.payoutType} ·{" "}
								{d.payoutAmount != null
									? `${d.payoutAmount} ${d.payoutCurrency}`
									: `${d.payoutPercent}% ${d.payoutCurrency}`}
							</div>
							<div className="font-mono text-xs text-neutral-500">{d.id}</div>
						</div>
						<div className="text-xs">
							state: <span className="font-mono">{d.state}</span>
						</div>
					</div>
					<div className="mt-2 font-mono text-xs text-neutral-600">
						contractHash {d.contractHash.slice(0, 16)}…
					</div>
				</Link>
			))}
		</div>
	);
}

// DealSignPanel kept here because it's also used by Negotiations.tsx for
// post-acceptance signing flow.
export function DealSignPanel(props: {
	negotiationId: string;
	offerId: string;
	brandOrgId: string;
	affiliateOrgId: string;
	payoutType: "cpa" | "cpl" | "revshare" | "hybrid";
	payoutAmount: number | null;
	payoutPercent: number | null;
	payoutCurrency: string;
	side: "brand" | "affiliate";
}) {
	const qc = useQueryClient();
	const [signer, setSigner] = useState("");
	const [status, setStatus] = useState<string | null>(null);

	const submitMut = useMutation({
		mutationFn: async () => {
			const now = Math.floor(Date.now() / 1000);
			const dealId = crypto.randomUUID();
			const payload = {
				dealId,
				offerId: props.offerId,
				brandOrgId: props.brandOrgId,
				affiliateOrgId: props.affiliateOrgId,
				payout: {
					type: props.payoutType,
					amount: props.payoutAmount,
					percent: props.payoutPercent,
					currency: props.payoutCurrency,
				},
				startsAt: now,
				endsAt: null,
				createdAt: now,
			};
			const canon = JSON.stringify(payload, Object.keys(payload).sort());
			const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canon));
			const signature = toHex(new Uint8Array(buf));
			return api.post<{
				ok: true;
				awaiting?: "brand" | "affiliate";
				dealId?: string;
				contractHash?: string;
			}>("/deals/sign", {
				negotiationId: props.negotiationId,
				side: props.side,
				signer,
				signature,
				payload,
			});
		},
		onSuccess: (data) => {
			if (data.dealId) {
				setStatus(`Signed. Contract committed: ${data.contractHash?.slice(0, 24)}…`);
			} else if (data.awaiting) {
				setStatus(`Signed. Awaiting ${data.awaiting} signature.`);
			}
			qc.invalidateQueries({ queryKey: ["deals"] });
		},
	});

	return (
		<div className="rounded border border-emerald-300 bg-emerald-50 p-3">
			<div className="text-sm font-medium text-emerald-900">Sign as {props.side}</div>
			<div className="mt-1 text-xs text-emerald-800">
				SHA-256 of the canonical payload as a fake EIP-712-shaped signature. Real wallet signing
				comes in M3+.
			</div>
			<input
				placeholder="Your name / wallet handle"
				value={signer}
				onChange={(e) => setSigner(e.target.value)}
				className="mt-2 w-full rounded border px-2 py-1 text-sm"
			/>
			<button
				type="button"
				disabled={!signer.trim() || submitMut.isPending}
				onClick={() => submitMut.mutate()}
				className="mt-2 rounded bg-emerald-600 px-3 py-1 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
			>
				Sign deal
			</button>
			{submitMut.error && (
				<div className="mt-2 text-xs text-red-700">{(submitMut.error as Error).message}</div>
			)}
			{status && <div className="mt-2 text-xs text-emerald-800">{status}</div>}
		</div>
	);
}

function toHex(buf: Uint8Array): string {
	let out = "";
	for (const b of buf) out += b.toString(16).padStart(2, "0");
	return out;
}
